 
import React from "react";
import { api } from "../../api/api";
import {
  CustomFormLabel,
  CustomShrinkTextField,
  CustomTextField,
} from "../formsComponents";
import Autocomplete from "@mui/material/Autocomplete";
import { IUsersAutoSearchByRole } from "./AutoCompleteSearches.types";
import { IUserProfile } from "../../redux/UserProfileList/userProfile.types";
import { CircularProgress, useTheme } from "@mui/material";
import { debounce } from "lodash";

const INITIAL_STATE: IUserProfile = {
  first_name: "",
  last_name: "",
  username: "",
  user_code: "",
  email: "",
  config_json: null,
  role_id: 0,
  status: "ACTIVE",
  user_fact_id: null,
  account: null,
  branch_code: null,
  application_number: null,
  assigned_phone_number: null,
  bank: null,
  bank_name: null,
  bonus_expense_code: null,
  bonus_liability_code: null,
  business_number: null,
  csr: null,
  employment_status: null,
  languages_known: null,
  branch: null,
  broker: null,
  city: null,
  commission_expense_code: null,
  commission_liability_code: null,
  country: null,
  created_by_id: null,
  documents: null,
  insert_ts: null,
  location_city: null,
  manager_id: null,
  marketer: null,
  non_active_firm: null,
  non_active_firm_name: null,
  non_active_firm_registered_with_ribo: null,
  personal_email: null,
  postal_code: null,
  province_or_state: null,
  report_email: null,
  shared_email: [],
  producer_one: null,
  csr_code: null, 
  marketer_code: null,
  producer_one_code: null,
  producer_two: null,
  producer_two_code: null,
  producer_three: null,
  producer_three_code: null,
  date_of_birth: null,
  employee_number: null,
  defaults_role: null,
  department: null,
  fax: null,
  full_name: null,
  hire_date: null,
  home_phone: null,
  job_title: null,
  last_day_at_work: null,
  linkedin_profile: null,
  mobile: null,
  mother_maiden_name: null,
  phone_ext: null,
  reports_to: null,
  signature: null,
  sin: null,
  street_address: null,
  unit_or_suite: null,
  user_type: null,
  notification_email: null,
  photo: null,
  transit: null,
  user_profile_id: 0,
};

export const UsersAutoSearchByRole: React.FC<IUsersAutoSearchByRole> = (props) => {
  const {
    value,
    label,
    selectionBy = "user_fact_id",
    disabled,
    errorMessage,
    placeholder,
    fullWidth = true,
    hasShrinkTextField,
    shrinkTextFieldSize,
    sx
  } = props;

  const [usersList, setUsersList] = React.useState<IUserProfile[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const initialLoad = React.useRef(false);
  const theme = useTheme();

  // ✅ wrap in useCallback to keep same instance
  const fetchUsers = React.useCallback(
    debounce(async (val: string) => {
      try {
        setLoading(true);
        const res = await api.get(
          `/user/get-users?status=ACTIVE&column=username&column=first_name&column=user_code&column=user_fact_id&value=${val}&pageNo=1&itemPerPage=10`
        );
        const data: IUserProfile[] = res.data.data;
        const finalData: IUserProfile[] = [...data];

        const exists = finalData.some((user) =>
          selectionBy === "user_fact_id"
            ? user.user_fact_id === Number(value)
            : user.user_code === value
        );

        if (!exists && value) {
          const fallbackUser: IUserProfile = {
            ...INITIAL_STATE,
            user_code:
              selectionBy === "user_fact_id" ? `${value} (ID)` : `${value} (Code)`,
            [selectionBy]:
              selectionBy === "user_fact_id" ? Number(value) : value,
          };
          setUsersList((prev) => [...finalData, fallbackUser]);
        } else {
          setUsersList(finalData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }, 500), // ⏳ debounce 500ms
    [selectionBy, value]
  );

  // cleanup on unmount to cancel pending debounce
  React.useEffect(() => {
    return () => {
      fetchUsers.cancel();
    };
  }, [fetchUsers]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    fetchUsers(val);
    initialLoad.current = true;
  };

  const getOptionLabel = (option: IUserProfile) => {
    return `${option.user_code} ${
      option.first_name || option.last_name
        ? `(${option.first_name || ""} ${option.last_name || ""})`
        : ""
    } `;
  };

  const getValue = () => {
    if (selectionBy === "user_fact_id") {
      return (
        usersList.find((option) => option.user_fact_id === Number(value)) || null
      );
    }
    return usersList.find((option) => option.user_code === value) || null;
  };

  React.useEffect(() => {
    if (value && !initialLoad.current) {
      setSearchText(value);
      fetchUsers(value); // initial fetch only
    } else if (!value) {
      setSearchText("");
    }
  }, [value, fetchUsers]);

  return (
    <>
      {label && !hasShrinkTextField && (
        <CustomFormLabel>{label}</CustomFormLabel>
      )}
      <Autocomplete
        options={usersList}
        disabled={disabled}
        fullWidth={fullWidth}
        sx={{
          ".MuiOutlinedInput-root": {
            paddingTop: "0px",
            paddingBottom: "0px",
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
          ...sx
        }}
        value={getValue()}
        defaultValue={value}
        getOptionLabel={getOptionLabel}
        onChange={(e, newValue) => {
          if (newValue) {
            props.onSelect?.(newValue);
            return;
          }
          props.onSelect?.(INITIAL_STATE);
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input" || !newInputValue) {
            setSearchText(newInputValue || "");
            initialLoad.current = true;
            fetchUsers(newInputValue || "");
          }
        }}
        renderInput={(params) => {
          if (hasShrinkTextField) {
            return (
              <CustomShrinkTextField
                {...params}
                fullWidth
                placeholder={placeholder}
                label={label}
                size={shrinkTextFieldSize}
                error={!!errorMessage}
                helperText={errorMessage}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            );
          }
          return (
            <CustomTextField
              {...params}
              fullWidth
              placeholder={placeholder}
              error={!!errorMessage}
              helperText={errorMessage}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />
    </>
  );
};
